/**
 * Dialog shown when closing a notebook tab that has unsaved changes.
 * Offers three choices: Save, Don't Save, or Cancel.
 */

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';

type NotebookCloseDialogProps = {
  open: boolean;
  onSaveAndClose: () => void;
  onDiscardAndClose: () => void;
  onCancel: () => void;
  isSaving?: boolean;
};

export function NotebookCloseDialog({
  open,
  onSaveAndClose,
  onDiscardAndClose,
  onCancel,
  isSaving = false,
}: NotebookCloseDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent data-testid="notebook-close-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            This notebook has unsaved changes. Do you want to save before closing?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel onClick={onCancel} disabled={isSaving}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onDiscardAndClose}
            disabled={isSaving}
            data-testid="notebook-discard-and-close"
          >
            Don&apos;t Save
          </Button>
          <Button
            onClick={onSaveAndClose}
            disabled={isSaving}
            data-testid="notebook-save-and-close"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
